/**
 * admin-app queryClient
 * Extends shared base — no workspace headers, no /tba/ routes, no subscription expiry.
 */
import { QueryClient, QueryFunction } from "@tanstack/react-query";
import {
  initApiUrl,
  throwIfResNotOk,
  getBaseAuthHeaders,
  apiRequest as sharedApiRequest,
} from "@chefaiq/shared/lib/queryClient";

export let API_URL = import.meta.env.VITE_API_URL || "";
initApiUrl(API_URL);

export { getBaseAuthHeaders as getAuthHeaders };

type UnauthorizedBehavior = "returnNull" | "throw";

export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    const endpoint = (queryKey as string[]).join("/");
    const fullUrl = endpoint.startsWith("http") ? endpoint : `${API_URL}${endpoint}`;

    const res = await fetch(fullUrl, { headers: getBaseAuthHeaders() });

    if (res.status === 401 || res.status === 403) {
      if (window.location.pathname === "/signin") {
        return unauthorizedBehavior === "returnNull" ? null : (() => { throw new Error("Unauthorized"); })();
      }
      localStorage.removeItem("logging_in");
      if (!localStorage.getItem("logging_out")) {
        localStorage.removeItem("auth_token");
        localStorage.removeItem("auth_user");
        localStorage.removeItem("current-workspace-id");
        localStorage.removeItem("workspaces");
        localStorage.removeItem("subscription_expired");
        document.cookie = "auth_token=; path=/; max-age=0";
        window.location.href = "/signin";
        return null;
      }
      return null;
    }

    await throwIfResNotOk(res);
    return await res.json();
  };

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: true,
      refetchOnMount: true,
      refetchOnReconnect: true,
      staleTime: 30 * 1000,
      gcTime: 5 * 60 * 1000,
      retry: false,
    },
    mutations: { retry: false },
  },
});

// apiRequest bound to admin-app's auth headers (no workspace headers)
export function apiRequest(
  method: string,
  url: string,
  data?: unknown,
  customHeaders?: Record<string, string>
): Promise<unknown> {
  return sharedApiRequest(method, url, data, customHeaders, getBaseAuthHeaders);
}
